/**
 * Stage 73.20cu — Enhanced Employee Profile Page (ESS side).
 * ----------------------------------------------------------------
 * Shows every profile section (personal, employment, salary, statutory,
 * bank, nominees) for the logged-in employee. Bank account is masked
 * to the last 4 digits. All fields are read-only. Any edit goes
 * through the Request-Change workflow — a modal captures the desired
 * value + reason and posts to /api/ess/profile/change-request, which
 * an HR admin can approve/reject from the HR Approvals page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Loader2, User, Briefcase, Landmark, Shield, Users as UsersIcon,
  Edit3, X, Send, Building2, Wallet, KeyRound, Info, GraduationCap,
  Eye, EyeOff, CheckCircle2,
} from 'lucide-react';
import { AuthenticatedImage } from '../components/AuthenticatedImage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SectionCard = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
      <Icon className="w-4 h-4 text-[#0052FF]" />
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    </div>
    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
      {children}
    </div>
  </div>
);

const Field = ({ label, value, path, onEdit, editable = true, testid, extraRight }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 last:border-0" data-testid={testid}>
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <div className="flex items-center gap-2 mt-0.5">
        <p className="text-sm text-slate-900 font-medium break-words">{value || <span className="text-slate-400 font-sans">—</span>}</p>
        {extraRight}
      </div>
    </div>
    {editable && onEdit && (
      <button
        onClick={() => onEdit(path, label, value)}
        data-testid={`request-change-${path.replace(/\./g, '-')}`}
        className="shrink-0 mt-0.5 text-[11px] text-[#0052FF] hover:underline flex items-center gap-1"
        title="Request HR to change this value"
      >
        <Edit3 className="w-3 h-3" />
        Request change
      </button>
    )}
  </div>
);

export default function EmployeeProfile() {
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBankAcc, setShowBankAcc] = useState(true);

  // Change-request modal
  const [modal, setModal] = useState({ open: false, path: '', label: '', current: '' });
  const [reqForm, setReqForm] = useState({ requested_value: '', reason: '' });
  const [reqSaving, setReqSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API_URL}/ess/profile/full`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/profile/change-requests`, { withCredentials: true }),
      ]);
      setProfile(p.data);
      setRequests(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (path, label, current) => {
    setModal({ open: true, path, label, current: current || '' });
    setReqForm({ requested_value: '', reason: '' });
  };

  const submitRequest = async () => {
    if (!reqForm.requested_value.trim()) { toast.error('Please enter the new value'); return; }
    setReqSaving(true);
    try {
      await axios.post(
        `${API_URL}/ess/profile/change-request`,
        {
          field_path: modal.path,
          current_value: String(modal.current || ''),
          requested_value: reqForm.requested_value.trim(),
          reason: reqForm.reason.trim(),
        },
        { withCredentials: true },
      );
      toast.success('Change request sent to HR');
      setModal({ open: false, path: '', label: '', current: '' });
      setReqForm({ requested_value: '', reason: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setReqSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      </MainLayout>
    );
  }

  if (!profile) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto py-16">
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4">
            No employee record is linked to your account yet. Please contact your HR administrator.
          </div>
        </div>
      </MainLayout>
    );
  }

  const p = profile.personal_details;
  const e = profile.employment_details;
  const s = profile.salary_details;
  const k = profile.kyc_statutory_details;
  const b = profile.bank_details;
  const nominees = profile.nominees || [];

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="employee-profile-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">My Profile</h1>
            <p className="mt-1 text-sm text-slate-600">All your details on file. To change any read-only value, use “Request change”.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {profile.pending_change_requests > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-semibold" data-testid="pending-requests-badge">
                <Info className="w-3 h-3" /> {profile.pending_change_requests} pending change request{profile.pending_change_requests > 1 ? 's' : ''}
              </span>
            )}
            <a
              href="/ess/statutory-forms"
              data-testid="link-statutory-forms"
              className="inline-flex items-center gap-1 rounded-lg bg-[#0052FF] hover:bg-[#0043D1] text-white px-4 py-2 text-sm font-medium"
            >
              <KeyRound className="w-4 h-4" /> Statutory Forms
            </a>
          </div>
        </div>

        {/* Profile Summary Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="flex items-center gap-6">
            <Avatar className="h-20 w-20 border-4 border-slate-50 shadow-md flex items-center justify-center overflow-hidden">
              <AuthenticatedImage 
                src={p.photoUrl || p.photo_url || profile.photoUrl} 
                alt={`${p.first_name} ${p.last_name}`} 
                className="h-full w-full object-cover rounded-full" 
                fallbackText={((p.first_name?.[0] || '') + (p.last_name?.[0] || '')).toUpperCase() || 'EMP'}
              />
            </Avatar>
            <div className="text-center md:text-left space-y-1">
              <h2 className="text-xl font-bold text-slate-900">{p.first_name} {p.last_name}</h2>
              <p className="text-sm font-medium text-[#0052FF]">{e.designation_name || 'Designation Not Set'}</p>
              <p className="text-xs text-slate-500 font-mono">Code: {e.employee_code || '—'} · {e.company_name}</p>
            </div>
          </div>
          {profile.profile_completion && (
            <div className="w-full md:w-80 bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-700">Profile Completion</span>
                <span className="font-bold text-[#0052FF]">{profile.profile_completion.percentage}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-[#0052FF] h-full rounded-full transition-all duration-500"
                  style={{ width: `${profile.profile_completion.percentage}%` }}
                />
              </div>
              <div className="flex flex-wrap gap-1 pt-1 text-[10px]">
                {(profile.profile_completion.completed_sections || []).map((sec) => (
                  <span key={sec} className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">✓ {sec}</span>
                ))}
                {(profile.profile_completion.pending_sections || []).map((sec) => (
                  <span key={sec} className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">⏳ {sec}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Personal */}
        <SectionCard icon={User} title="Personal Details" testid="section-personal">
          <Field label="First Name" value={p.first_name} path="personal_details.first_name" onEdit={openEdit} />
          <Field label="Last Name" value={p.last_name} path="personal_details.last_name" onEdit={openEdit} />
          <Field label="Date of Birth" value={p.date_of_birth} path="personal_details.date_of_birth" onEdit={openEdit} />
          <Field label="Gender" value={p.gender} path="personal_details.gender" onEdit={openEdit} />
          <Field label="Marital Status" value={p.marital_status} path="personal_details.marital_status" onEdit={openEdit} />
          <Field label="Blood Group" value={p.blood_group} path="personal_details.blood_group" onEdit={openEdit} />
          <Field label="Mobile" value={p.mobile} path="personal_details.mobile" onEdit={openEdit} />
          <Field label="Personal Email" value={p.personal_email} path="personal_details.personal_email" onEdit={openEdit} />
          <Field label="Emergency Contact Name" value={p.emergency_contact_name} path="personal_details.emergency_contact_name" onEdit={openEdit} />
          <Field label="Emergency Contact Number" value={p.emergency_contact_number} path="personal_details.emergency_contact_number" onEdit={openEdit} />
          <Field label="Current Address" value={p.current_address} path="personal_details.current_address" onEdit={openEdit} />
          <Field label="Permanent Address" value={p.permanent_address} path="personal_details.permanent_address" onEdit={openEdit} />
          <Field label="City" value={p.city} path="personal_details.city" onEdit={openEdit} />
          <Field label="State" value={p.state} path="personal_details.state" onEdit={openEdit} />
          <Field label="Pincode" value={p.pincode} path="personal_details.pincode" onEdit={openEdit} />
        </SectionCard>

        {/* Employment */}
        <SectionCard icon={Briefcase} title="Employment" testid="section-employment">
          <Field label="Employee Code" value={e.employee_code} editable={false} path="employment_details.employee_code" />
          <Field label="Company" value={e.company_name} editable={false} path="employment_details.company_id" />
          <Field label="Designation" value={e.designation_name} editable={false} path="employment_details.designation_id" />
          <Field label="Department" value={e.department_name} editable={false} path="employment_details.department_id" />
          <Field label="Grade" value={e.grade_name} editable={false} path="employment_details.grade_id" />
          <Field label="Location" value={e.location_name} editable={false} path="employment_details.location_id" />
          <Field label="Date of Joining" value={e.date_of_joining} editable={false} path="employment_details.date_of_joining" />
          <Field label="Confirmation Date" value={e.confirmation_date} editable={false} path="employment_details.confirmation_date" />
          <Field label="Employment Status" value={e.employment_status} editable={false} path="employment_details.employment_status" />
          <Field label="Work Email" value={e.work_email} editable={false} path="employment_details.work_email" />
        </SectionCard>

        {/* Salary */}
        <SectionCard icon={Wallet} title="Salary Structure" testid="section-salary">
          <Field label="Structure Option" value={s.structure_code === 'MANUAL' || s.structure_source === 'manual' ? '✨ MANUAL' : (s.structure_code || 'Standard')} editable={false} path="salary_details.structure_code" />
          <Field label="CTC (Annual)" value={s.ctc_annual ? `₹ ${Number(s.ctc_annual).toLocaleString('en-IN')}` : ''} editable={false} path="salary_details.ctc_annual" />
          <Field label="Gross (Monthly)" value={s.gross_monthly ? `₹ ${Number(s.gross_monthly).toLocaleString('en-IN')}` : ''} editable={false} path="salary_details.gross_monthly" />
          <Field label="Basic (Monthly)" value={s.basic_monthly ? `₹ ${Number(s.basic_monthly).toLocaleString('en-IN')}` : ''} editable={false} path="salary_details.basic_monthly" />
          <Field label="Effective From" value={s.effective_from} editable={false} path="salary_details.effective_from" />
          {s.components && s.components.length > 0 && (
            <div className="col-span-1 md:col-span-2 pt-2 mt-2 border-t border-slate-100">
              <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500 mb-2">Salary Component Breakup</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {s.components.map((c, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                    <p className="font-semibold text-slate-800 truncate">{c.head_name || c.head_code}</p>
                    <p className="text-emerald-600 font-mono font-medium mt-0.5">₹ {(c.monthly_amount || 0).toLocaleString('en-IN')}/mo</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>

        {/* Statutory IDs */}
        <SectionCard icon={Shield} title="Statutory IDs" testid="section-statutory">
          <Field label="PAN" value={k.pan_number} path="kyc_statutory_details.pan_number" onEdit={openEdit} />
          <Field label="Aadhaar" value={k.aadhaar_number} path="kyc_statutory_details.aadhaar_number" onEdit={openEdit} />
          <Field label="UAN" value={k.uan_number} path="kyc_statutory_details.uan_number" onEdit={openEdit} />
          <Field label="ESIC No." value={k.esi_number} path="kyc_statutory_details.esi_number" onEdit={openEdit} />
          <Field label="PF Code" value={k.pf_code} editable={false} path="kyc_statutory_details.pf_code" />
          <Field label="ESI Code" value={k.esi_code} editable={false} path="kyc_statutory_details.esi_code" />
        </SectionCard>

        {/* Bank */}
        <SectionCard icon={Landmark} title="Bank Details" testid="section-bank">
          <Field label="Bank Name" value={b.bank_name} path="bank_details.bank_name" onEdit={openEdit} />
          <Field
            label="Account Number"
            value={showBankAcc ? (b.account_number || b.account_number_masked) : (b.account_number_masked || (b.account_number ? `••••••••${b.account_number.slice(-4)}` : '—'))}
            path="bank_details.account_number"
            onEdit={openEdit}
            testid="field-bank-account-number"
            extraRight={
              (b.account_number || b.account_number_masked) ? (
                <button
                  type="button"
                  onClick={() => setShowBankAcc(!showBankAcc)}
                  className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition"
                  title={showBankAcc ? "Hide account number" : "Show full account number"}
                  aria-label="Toggle bank account visibility"
                >
                  {showBankAcc ? <EyeOff className="w-3.5 h-3.5 text-slate-600" /> : <Eye className="w-3.5 h-3.5 text-slate-500" />}
                </button>
              ) : null
            }
          />
          <Field label="IFSC" value={b.ifsc_code} path="bank_details.ifsc_code" onEdit={openEdit} />
          <Field label="Branch" value={b.bank_branch} path="bank_details.bank_branch" onEdit={openEdit} />
          <Field label="Account Holder Name" value={b.account_holder_name} path="bank_details.account_holder_name" onEdit={openEdit} />
        </SectionCard>

        {/* Nominees */}
        <SectionCard icon={UsersIcon} title={`Nominees (${nominees.length})`} testid="section-nominees">
          {nominees.length === 0 && <p className="col-span-2 text-sm text-slate-500 italic">No nominees on file. Complete Form F / Gratuity nomination in Statutory Forms.</p>}
          {nominees.map((n, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-slate-800">{n.name}</p>
              <p className="text-xs text-slate-500">{n.relationship} · {n.percentage}% share</p>
              <p className="text-xs text-slate-500 mt-1">{n.address}</p>
            </div>
          ))}
        </SectionCard>

        {/* Education Details */}
        <SectionCard icon={GraduationCap} title={`Education Details (${(profile.education || []).length})`} testid="section-education">
          {(profile.education || []).length === 0 ? (
            <p className="col-span-2 text-sm text-slate-500 italic">No education records on file.</p>
          ) : (
            <div className="col-span-2 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-700 uppercase font-semibold">
                  <tr>
                    <th className="p-2.5">Qualification</th>
                    <th className="p-2.5">Degree</th>
                    <th className="p-2.5">Institution</th>
                    <th className="p-2.5">University / Board</th>
                    <th className="p-2.5">Specialisation</th>
                    <th className="p-2.5">Year</th>
                    <th className="p-2.5">% / CGPA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {(profile.education || []).map((q, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="p-2.5 font-semibold text-slate-900">{q.qualification || q.degree || '—'}</td>
                      <td className="p-2.5 font-medium">{q.degree || q.qualification || '—'}</td>
                      <td className="p-2.5">{q.institution || q.college || '—'}</td>
                      <td className="p-2.5">{q.university || q.board || '—'}</td>
                      <td className="p-2.5">{q.specialisation || '—'}</td>
                      <td className="p-2.5 font-mono">{q.year_of_passing || q.passing_year || q.year || '—'}</td>
                      <td className="p-2.5 font-mono font-semibold text-emerald-600">{q.percentage || q.cgpa || q.grade || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* Previous Employment History */}
        <SectionCard icon={Briefcase} title={`Previous Employment History (${(profile.previous_employers || []).length})`} testid="section-previous-employers">
          {(profile.previous_employers || []).length === 0 ? (
            <p className="col-span-2 text-sm text-slate-500 italic">No previous employment records listed.</p>
          ) : (
            <div className="col-span-2 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-700 uppercase font-semibold">
                  <tr>
                    <th className="p-2.5">Employer Name</th>
                    <th className="p-2.5">Designation</th>
                    <th className="p-2.5">Dept / Type</th>
                    <th className="p-2.5">From - To Date</th>
                    <th className="p-2.5">Calculated Experience</th>
                    <th className="p-2.5">Last Salary</th>
                    <th className="p-2.5">Reason for Leaving</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {(profile.previous_employers || []).map((w, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="p-2.5 font-medium text-slate-900">{w.employer_name || w.company || w.company_name || '—'}</td>
                      <td className="p-2.5">{w.designation || '—'}</td>
                      <td className="p-2.5">{w.department || w.employment_type || '—'}</td>
                      <td className="p-2.5 font-mono">{w.from_date || '—'} to {w.to_date || '—'}</td>
                      <td className="p-2.5 font-semibold text-[#0052FF]">{w.experience || '—'}</td>
                      <td className="p-2.5 font-mono">{w.salary ? `₹ ${w.salary}` : (w.last_salary ? `₹ ${Number(w.last_salary).toLocaleString('en-IN')}` : '—')}</td>
                      <td className="p-2.5">{w.reason || w.reason_for_leaving || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* My change requests */}
        <SectionCard icon={Building2} title={`My Change Requests (${requests.length})`} testid="section-requests">
          {requests.length === 0 && <p className="col-span-2 text-sm text-slate-500 italic">You haven’t submitted any change requests yet.</p>}
          {requests.length > 0 && (
            <div className="col-span-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="py-2 pr-3">Field</th>
                    <th className="py-2 pr-3">Requested</th>
                    <th className="py-2 pr-3">Reason</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`request-row-${r.id}`}>
                      <td className="py-2 pr-3 text-slate-700 font-mono text-xs">{r.field_path}</td>
                      <td className="py-2 pr-3 text-slate-900">{r.requested_value}</td>
                      <td className="py-2 pr-3 text-slate-500 text-xs">{r.reason || '—'}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                          r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                        }`}>{r.status}</span>
                      </td>
                      <td className="py-2 pr-3 text-slate-500 text-xs">{(r.submitted_at || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Request-change modal */}
      {modal.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="request-change-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-900">Request change · {modal.label}</h2>
              <button onClick={() => setModal({ open: false, path: '', label: '', current: '' })} className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500">Current value</label>
                <p className="mt-1 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-700 break-words">{modal.current || <span className="text-slate-400">— empty —</span>}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">New value *</label>
                <input
                  type="text"
                  value={reqForm.requested_value}
                  onChange={e => setReqForm({ ...reqForm, requested_value: e.target.value })}
                  data-testid="req-change-new-value"
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0052FF]"
                  placeholder="e.g. new mobile / IFSC / address"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Reason (optional)</label>
                <textarea
                  rows={3}
                  value={reqForm.reason}
                  onChange={e => setReqForm({ ...reqForm, reason: e.target.value })}
                  data-testid="req-change-reason"
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0052FF]"
                  placeholder="Why do you want this change?"
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                An HR administrator will review this request. Nothing changes on your profile until they approve it.
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button variant="outline" onClick={() => setModal({ open: false, path: '', label: '', current: '' })}>Cancel</Button>
              <Button
                onClick={submitRequest}
                disabled={reqSaving}
                data-testid="submit-change-request"
                className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
              >
                {reqSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
